// Pure helpers shared by the builder and the record pages. Kept free of API
// imports so they run under Jest. The type lists mirror CustomModuleController.

export const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Long Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date & Time" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "url", label: "URL" },
  { value: "select", label: "Dropdown" },
  { value: "multiselect", label: "Multi Select" },
  { value: "checkbox", label: "Checkbox" },
  { value: "autocomplete", label: "Autocomplete" },
  { value: "repeater", label: "Add More (Repeating Group)" },
  { value: "attachment", label: "File Upload" },
];

// "Allowed files" for an attachment field. Same lists as
// CustomModuleController::FILE_KINDS, which also checks each file's content.
const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp"];
const DOCUMENT_EXTS = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv", "txt"];
export const FILE_KINDS = {
  any: { label: "Any common file (images, documents, zip)", exts: [...IMAGE_EXTS, ...DOCUMENT_EXTS, "zip"] },
  images: { label: "Images only (JPG, PNG, GIF, WEBP)", exts: IMAGE_EXTS },
  pdf: { label: "PDF only", exts: ["pdf"] },
  images_pdf: { label: "Images & PDF", exts: [...IMAGE_EXTS, "pdf"] },
  documents: { label: "Documents (PDF, Word, Excel, PowerPoint, CSV, TXT)", exts: DOCUMENT_EXTS },
};
export const MAX_FILES = 10;
export const MAX_FILE_MB = 10;

// Columns a repeater row may have.
export const SUB_FIELD_TYPES = FIELD_TYPES.filter((type) =>
  ["text", "number", "date", "email", "phone", "url", "select"].includes(type.value)
);

export const OPTION_TYPES = ["select", "multiselect"];
export const FILTER_TYPES = ["select", "multiselect", "checkbox", "date", "datetime", "autocomplete"];
export const CONDITION_TYPES = ["select", "multiselect", "checkbox"];
export const LENGTH_TYPES = ["text", "textarea", "phone", "url"];
export const NO_UNIQUE_TYPES = ["checkbox", "multiselect", "repeater", "attachment"];
export const NO_ACTION_TYPES = ["autocomplete", "multiselect", "repeater", "attachment"];
export const NO_DEFAULT_TYPES = ["autocomplete", "repeater", "attachment"];
export const PAGE_SIZES = [10, 20, 50, 100];

export const ACTION_COLORS = {
  primary: { label: "Blue", color: "#3D4AF5" },
  success: { label: "Green", color: "#17B65B" },
  warning: { label: "Amber", color: "#D98E04" },
  danger: { label: "Red", color: "#D61916" },
  secondary: { label: "Grey", color: "#546088" },
};

export const DEFAULT_SECTION = { key: "main", title: "Details", columns: 2 };

// Where an autocomplete field searches, mapped to the existing suggestion
// endpoints (all return { suggestionlist: [{ value, label }] }).
export const AUTOCOMPLETE_SOURCES = [
  { value: "customer", label: "Customers", path: "get_customers" },
  { value: "lead", label: "Leads", path: "get_leads" },
  { value: "contact", label: "Contacts", path: "get_contacts" },
  { value: "employee", label: "Employees", path: "get_lead_owner" },
  { value: "item", label: "Items", path: "get_products" },
];

export const CUSTOM_BASES = [
  { value: "api", label: "CRM API" },
  { value: "admin", label: "Admin API" },
];

// "api:<path>" / "admin:<path>" is a custom suggestion URL. Only a path on our
// own APIs is allowed, never a full URL: the request carries the user's token.
// Same rule as CustomModuleController::CUSTOM_SOURCE.
const CUSTOM_PATH = /^[A-Za-z0-9_][A-Za-z0-9_\-/]*(\?[A-Za-z0-9_=&.,%-]*)?$/;

export const parseCustomSource = (source = "") => {
  const match = /^(api|admin):(.*)$/.exec(source);
  return match ? { base: match[1], path: match[2] } : null;
};

export const isValidCustomPath = (path) => CUSTOM_PATH.test(path) && !path.includes("//");

// Full URL the autocomplete dropdown calls, or null when there is nothing safe
// to call. "module:<slug>" searches the records of another custom module.
export const sourceUrl = (source = "") => {
  const custom = parseCustomSource(source);
  if (custom) {
    if (!isValidCustomPath(custom.path)) return null;
    return (custom.base === "admin" ? process.env.REACT_APP_ADMIN_API_URL : process.env.REACT_APP_API_URL) + custom.path;
  }
  const path = source.startsWith("module:")
    ? `custom_module/${source.slice(7)}/suggestions`
    : AUTOCOMPLETE_SOURCES.find((item) => item.value === source)?.path;
  return path ? process.env.REACT_APP_API_URL + path : null;
};

// Hidden fields are kept on the record (set by defaults and buttons) but never
// appear on a page: not the form, the list, the detail view or the export.
export const isShown = (field) => !field.hidden;

// A module's page settings with every default filled in, so pages never guard
// against modules saved before a setting existed. Text settings stay raw ("").
export const settingsOf = (module) => {
  const raw = module?.settings && !Array.isArray(module.settings) ? module.settings : {};
  const fields = (module?.fields ?? []).filter(isShown);
  const list = raw.list ?? {};
  const legacyColumns = fields.filter((field) => field.in_list).map((field) => field.key);
  return {
    singular: raw.singular ?? "",
    description: raw.description ?? "",
    code_prefix: raw.code_prefix ?? "",
    sections: raw.sections?.length ? raw.sections : [DEFAULT_SECTION],
    list: {
      columns: list.columns?.length
        ? list.columns
        : legacyColumns.length
          ? legacyColumns
          : fields.slice(0, 5).map((field) => field.key),
      sort_field: list.sort_field ?? "",
      sort_dir: list.sort_dir ?? "desc",
      filters: list.filters ?? [],
      page_size: list.page_size ?? 10,
      board_field: list.board_field ?? "",
    },
    view: { history: raw.view?.history ?? true, related: raw.view?.related ?? true },
  };
};

export const singularOf = (module) => settingsOf(module).singular || module?.name || "";

export const sectionKeyOf = (field, sections) =>
  sections.some((section) => section.key === field.section) ? field.section : sections[0].key;

export const listColumns = (module) =>
  settingsOf(module)
    .list.columns.map((key) => module.fields.find((field) => field.key === key))
    .filter((field) => field && isShown(field));

export const defaultsOf = (module) =>
  Object.fromEntries(
    module.fields
      .filter((field) => field.default !== null && field.default !== undefined && field.default !== "")
      .map((field) => [field.key, field.default])
  );

export const matches = (value, expected) => {
  if (Array.isArray(value)) return value.includes(expected);
  if (typeof expected === "boolean") return value === true || value === "true" ? expected : !expected;
  return String(value ?? "") === String(expected ?? "");
};

// Same rule as CustomModuleController::isVisible: visible when the controlling
// field is itself visible and holds the expected value; a cycle is hidden.
export const isVisible = (field, fields, values, seen = new Set()) => {
  const rule = field.visible_when;
  if (!rule?.field) return true;
  const controller = fields.find((item) => item.key === rule.field);
  if (!controller) return true;
  if (seen.has(field.key)) return false;
  seen.add(field.key);
  return isVisible(controller, fields, values, seen) && matches(values[controller.key], rule.value);
};

export const formatValue = (field, value) => {
  if (field.type === "checkbox") return value === true ? "Yes" : value === false ? "No" : "--";
  if (field.type === "autocomplete") return value?.label || "--";
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && !value.length)) return "--";
  if (field.type === "attachment") return value.map((file) => file.name).join(", ");
  if (field.type === "repeater") {
    // One line: a row's cells joined by " · ", rows by ", ".
    const rows = value.map((row) =>
      (field.subfields ?? [])
        .map((sub) => formatValue(sub, row?.[sub.key]))
        .filter((text) => text !== "--")
        .join(" · ")
    );
    return rows.filter(Boolean).join(", ") || "--";
  }
  if (Array.isArray(value)) return value.join(", ");
  if (field.type === "date") {
    // UTC so a bare YYYY-MM-DD never shifts a day in negative-offset timezones.
    return new Date(value).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
  }
  if (field.type === "datetime") {
    return new Date(value).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  }
  return String(value);
};

// Heading for a record: its first shown field, else its number.
export const titleField = (module) => module.fields.find(isShown);

export const recordTitle = (module, record) => {
  const first = titleField(module);
  const text = first ? formatValue(first, record.data?.[first.key]) : "--";
  return text !== "--" ? text : record.code || `#${record.id}`;
};

// Field validation errors arrive as { key: ["message"] }; the forms show the first.
export const firstErrors = (error) =>
  Object.fromEntries(Object.entries(error ?? {}).map(([key, value]) => [key, Array.isArray(value) ? value[0] : value]));

// Cells that start like a formula are prefixed so a spreadsheet shows them as
// text instead of running them (CSV injection); plain negative numbers are left alone.
export const toCsv = (rows) =>
  rows
    .map((row) =>
      row
        .map((cell) => {
          const text = String(cell ?? "");
          const safe = /^[=+\-@\t\r]/.test(text) && Number.isNaN(Number(text)) ? `'${text}` : text;
          return `"${safe.replace(/"/g, '""')}"`;
        })
        .join(",")
    )
    .join("\r\n");

export const downloadCsv = (filename, rows) => {
  const url = URL.createObjectURL(new Blob([`﻿${toCsv(rows)}`], { type: "text/csv;charset=utf-8" }));
  const link = Object.assign(document.createElement("a"), { href: url, download: filename });
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
